// Persisted, NON-SECRET hint that a CouchDB session existed on this device
// (#4027, originally groundwork for the "optimistic cold boot" decision).
//
// What it is: on every successful couch login, AuthService stores WHO logged
// in (couch user name, per-user db hex, roles, Firebase uid) and WHEN the
// server said the session cookie expires. The actual credential — the
// HttpOnly AuthSession cookie — stays in the browser / native cookie jar.
// This module must NEVER store the cookie value or a password; everything in
// the hint is metadata that already appears in plain console logs.
//
// Who reads this marker — BOTH of these, and the second one is easy to miss:
//
// 1. Telemetry. `booting_time` carries
//    `prior_session_hint: 'valid' | 'expired' | 'none'`, classified from a
//    sample taken at boot START (before the boot's own login overwrites the
//    marker). See design-docs/telemetry.md.
// 2. **The boot language.** `resolveBootLocale` (`client/src/i18n/ui-language.ts`,
//    added by #4284) treats "a marker exists" as "this device holds an
//    established signed-in account", and answers English instead of guessing
//    from the browser. That stops an English user on a French phone being
//    flipped to French on Home. So this is USER-VISIBLE behavior, not just a
//    metric: if you stop writing the marker on some login path, or clear it
//    somewhere new, you change what language signed-in users boot into — and
//    no test in THIS module would catch that.
//
// A third consumer was planned and will not arrive. "Optimistic cold boot"
// would have used this marker as its trigger to show the app before the couch
// login finished. It was REJECTED at #4166 on 2026-08-04, for reasons recorded
// in design-docs/boot-sequence.md ("The STOP-2 decision"). Do not extend this
// module for it without reopening that decision first — and note that one of
// the two reasons it was rejected is a property of this marker itself: a
// CouchDB session lasts 600 s from the last authenticated request, so on a
// real cold boot the stored session is nearly always already dead.
//
// Storage rules: localStorage, wiped on signOut BY OMISSION from
// local-storage-hygiene.ts's survivor allow-list (same rule as the Step-22
// `bootProgress` marker). Do NOT "helpfully" add this key to the allow-list:
// a hint surviving into the next account would misclassify that account's
// boots. The hygiene test pins this expectation.
//
// Pure module — no Angular / Sentry imports — unit-tested standalone with a
// localStorage shim (same pattern as warm-resume-storage.ts).

import { DateTime } from 'luxon';

// the localStorage key. literal is cross-checked by couch-session-hint.test.ts
// and local-storage-hygiene.test.ts (wipe-by-omission expectation).
export const COUCH_SESSION_HINT_KEY = 'cs_couch_session_hint';

// A session whose cookie expires within this margin is classified 'expired':
// it could lapse during the very boot that would rely on it, so the telemetry
// cohort should not count it as usable.
//
// This margin MUST stay well below the CouchDB session lifetime it is applied
// to (`couch_httpd_auth.timeout`, live-measured at 600 s — see
// design-docs/sync-integrity.md, F19). The reason is that the stored expiry is
// always (login time + that lifetime), so the remaining lifetime seen at a
// later boot is always less than the full lifetime. A margin equal to the
// lifetime therefore reduces the check to "is this boot earlier than the login
// that wrote the hint", which is never true — it makes 'valid' unreachable.
// That was the bug found at #4166: the margin was 10 minutes against a
// 10-minute session, so 5695 of 6883 production boots reported 'expired',
// including 5413 warm resumes moments after activity.
//
// 60 s is the value chosen there. It only has to cover boot start through the
// first authenticated request, which takes seconds and re-issues the cookie
// anyway. Its real floor is plausible device clock skew: the expiry comes from
// the SERVER's clock and `now` from the device's, so a margin far below the
// skew between them would misclassify in the other direction.
export const SESSION_HINT_EXPIRY_MARGIN_MS = 60 * 1000;

// classification reported on booting_time as `prior_session_hint`. The enum
// VALUE is the exact wire spelling — part of the Mixpanel contract, do not
// rename without updating design-docs/telemetry.md and any saved Mixpanel
// query that splits on this property.
export enum PriorSessionHint {
  // hint present, uid matches the logged-in user, expiry comfortably ahead
  Valid = 'valid',
  // hint present but unusable: expiry passed/near, uid mismatch, or no user
  Expired = 'expired',
  // no hint stored (fresh install, post-signout, or pre-rollout build)
  None = 'none',
}

export interface CouchSessionHint {
  // Firebase uid the session belongs to — the identity the next boot compares
  uid: string;
  // couch user name (login identifier, not a secret)
  user: string;
  // hex form of the couch user — the per-user database name suffix
  userHex: string;
  // couch roles at login time. nothing reads this today — it was stored for
  // the rejected optimistic-boot path (see the header), and is kept because
  // the marker is rewritten on every login, so dropping the field buys nothing
  roles: string[];
  // cookie expiry as the server reported it (HTTP-date via toUTCString)
  sessionExpires: string;
}

// persists the hint after a successful couch login — every successful login
// overwrites the previous marker, so the stored hint always describes the
// newest session. NOTE for callers: localStorage.setItem can throw (Safari
// private mode, full quota); the caller decides how to surface that, because
// this module is deliberately Sentry-free (see auth.service.ts's guarded call).
export function setCouchSessionHint(hint: CouchSessionHint): void {
  localStorage.setItem(COUCH_SESSION_HINT_KEY, JSON.stringify(hint));
}

// returns the stored hint only when every required field has the right type;
// half-written or tampered JSON yields null. Rebuilds the returned object
// from validated fields so extra keys in the raw JSON are dropped (same
// hardening as warm-resume-storage.getWarmResumeUserAuth).
export function getCouchSessionHint(): CouchSessionHint | null {
  const raw = localStorage.getItem(COUCH_SESSION_HINT_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<CouchSessionHint>;
    if (typeof parsed?.uid !== 'string' || parsed.uid === '') return null;
    if (typeof parsed.user !== 'string' || typeof parsed.userHex !== 'string'
        || typeof parsed.sessionExpires !== 'string') return null;
    if (!Array.isArray(parsed.roles) || parsed.roles.some(r => typeof r !== 'string')) return null;
    return {
      uid: parsed.uid,
      user: parsed.user,
      userHex: parsed.userHex,
      roles: parsed.roles,
      sessionExpires: parsed.sessionExpires,
    };
  } catch {
    // expected: SyntaxError from JSON.parse on a tampered or half-written
    // marker. Treat it as no hint — a corrupt marker must never break a boot,
    // and the next successful login overwrites it (self-healing), so there is
    // nothing actionable to report. Deliberately no telemetry: this module
    // stays Sentry-free (pure, node-testable). Note what "no hint" means for
    // each reader (see the header): telemetry reports 'none', and the boot
    // language falls back to the browser guess for that one boot.
    return null;
  }
}

// removes the marker. Nothing calls this today: the sign-out wipe already
// removes it via local-storage-hygiene's wipe-by-omission. It is exported so
// that a future caller needing an explicit clear keeps the key literal in one
// module. Before adding such a caller, read the header — clearing the marker
// also changes which language a signed-in user boots into.
export function clearCouchSessionHint(): void {
  localStorage.removeItem(COUCH_SESSION_HINT_KEY);
}

// Pure classifier for the `prior_session_hint` telemetry property.
// `hint` is the sample taken at boot start; `currentUid` is the user the boot
// actually logged in as (undefined/null on a logged-out boot); `now` is
// injectable for tests.
export function classifyPriorSessionHint(
  hint: CouchSessionHint | null,
  currentUid: string | null | undefined,
  now: DateTime,
): PriorSessionHint {
  if (!hint) return PriorSessionHint.None;
  // if the boot ended logged-out or as a different user, the stored session
  // could not have served this boot — classify as expired (unusable)
  if (!currentUid || hint.uid !== currentUid) return PriorSessionHint.Expired;
  // the server writes sessionExpires via Date.toUTCString() (HTTP-date);
  // accept ISO as a fallback so a future format change degrades gracefully
  let expires = DateTime.fromHTTP(hint.sessionExpires);
  if (!expires.isValid) expires = DateTime.fromISO(hint.sessionExpires);
  if (!expires.isValid) return PriorSessionHint.Expired;
  if (now.plus({ milliseconds: SESSION_HINT_EXPIRY_MARGIN_MS }) >= expires) return PriorSessionHint.Expired;
  return PriorSessionHint.Valid;
}
